
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import { getApiKeys, createApiKey, updateApiKey, getApiKeyUsageLogs } from '@/api/aim'

interface ApiKeyRecord {
  id: number
  appName: string
  keyPrefix: string
  permissions: string[]
  rateLimit: number
  isActive: boolean
  expiresAt: string | null
  lastUsedAt: string | null
  createdBy: string | null
  description: string | null
  createdAt: string
  updatedAt: string
}

interface UsageLog {
  id: number
  apiKeyId: number
  endpoint: string
  method: string
  statusCode: number | null
  ipAddress: string | null
  responseTimeMs: number | null
  createdAt: string
}

@Component({
  name: 'ExternalApiKeys'
})
export default class extends Vue {
  private list: ApiKeyRecord[] = []
  private total = 0
  private listLoading = false
  private listQuery = { page: 1, limit: 20 }

  private createDialogVisible = false
  private editDialogVisible = false
  private keyDisplayDialogVisible = false
  private logsDialogVisible = false
  private submitLoading = false

  private createdKeyPlaintext = ''

  private createFormData = {
    appName: '',
    description: '',
    permissions: [] as string[],
    rateLimit: 100,
    expiresAt: null as string | null
  }

  private editFormData = {
    id: null as number | null,
    appName: '',
    description: '',
    permissions: [] as string[],
    rateLimit: 100,
    isActive: true,
    expiresAt: null as string | null
  }

  private createRules = {
    appName: [
      { required: true, message: 'App name is required', trigger: 'blur' }
    ]
  }

  private editRules = {
    appName: [
      { required: true, message: 'App name is required', trigger: 'blur' }
    ]
  }

  // Logs
  private logsList: UsageLog[] = []
  private logsTotal = 0
  private logsLoading = false
  private logsAppName = ''
  private logsKeyId: number | null = null
  private logsQuery = { page: 1, limit: 50 }

  created() {
    this.getList()
  }

  private async getList() {
    this.listLoading = true
    try {
      const response = await getApiKeys(this.listQuery)
      const data = (response as any).data
      if (data.status.code === 'success') {
        this.list = data.data.apiKeys
        this.total = data.data.total
      }
    } catch (error) {
      this.$message.error('Failed to load API keys')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private handlePageChange(page: number) {
    this.listQuery.page = page
    this.getList()
  }

  private handleCreate() {
    this.createFormData = {
      appName: '',
      description: '',
      permissions: [],
      rateLimit: 100,
      expiresAt: null
    }
    this.createDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.createForm as ElForm)?.clearValidate()
    })
  }

  private async submitCreate() {
    (this.$refs.createForm as ElForm).validate(async (valid: boolean) => {
      if (!valid) return

      this.submitLoading = true
      try {
        const response = await createApiKey({
          appName: this.createFormData.appName.trim(),
          description: this.createFormData.description || undefined,
          permissions: this.createFormData.permissions,
          rateLimit: this.createFormData.rateLimit,
          expiresAt: this.createFormData.expiresAt || null
        })
        const data = (response as any).data
        if (data.status.code === 'success') {
          this.createdKeyPlaintext = data.data.apiKey
          this.createDialogVisible = false
          this.keyDisplayDialogVisible = true
          this.getList()
          this.$message.success('API key created successfully')
        } else {
          this.$message.error(data.status.message)
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.status?.message || 'Failed to create API key'
        this.$message.error(errorMessage)
        console.error(error)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private copyKey() {
    navigator.clipboard.writeText(this.createdKeyPlaintext).then(() => {
      this.$message.success('Copied to clipboard')
    }).catch(() => {
      this.$message.error('Failed to copy. Please copy manually.')
    })
  }

  private handleEdit(row: ApiKeyRecord) {
    this.editFormData = {
      id: row.id,
      appName: row.appName,
      description: row.description || '',
      permissions: row.permissions || [],
      rateLimit: row.rateLimit,
      isActive: row.isActive,
      expiresAt: row.expiresAt
    }
    this.editDialogVisible = true
  }

  private async submitEdit() {
    (this.$refs.editForm as ElForm).validate(async (valid: boolean) => {
      if (!valid) return

      this.submitLoading = true
      try {
        await updateApiKey(this.editFormData.id!, {
          appName: this.editFormData.appName,
          description: this.editFormData.description || undefined,
          permissions: this.editFormData.permissions,
          rateLimit: this.editFormData.rateLimit,
          isActive: this.editFormData.isActive,
          expiresAt: this.editFormData.expiresAt
        })
        this.editDialogVisible = false
        this.getList()
        this.$message.success('API key updated successfully')
      } catch (error: any) {
        const errorMessage = error.response?.data?.status?.message || 'Failed to update API key'
        this.$message.error(errorMessage)
        console.error(error)
      } finally {
        this.submitLoading = false
      }
    })
  }

  private async handleStatusChange(row: ApiKeyRecord) {
    try {
      await updateApiKey(row.id, { isActive: row.isActive })
      this.$message.success('Status updated')
    } catch (error) {
      row.isActive = !row.isActive
      this.$message.error('Failed to update status')
    }
  }

  private async handleViewLogs(row: ApiKeyRecord) {
    this.logsKeyId = row.id
    this.logsAppName = row.appName
    this.logsQuery.page = 1
    this.logsDialogVisible = true
    await this.fetchLogs()
  }

  private async fetchLogs() {
    if (!this.logsKeyId) return
    this.logsLoading = true
    try {
      const response = await getApiKeyUsageLogs(this.logsKeyId, this.logsQuery)
      const data = (response as any).data
      if (data.status.code === 'success') {
        this.logsList = data.data.logs
        this.logsTotal = data.data.total
      }
    } catch (error) {
      this.$message.error('Failed to load usage logs')
      console.error(error)
    } finally {
      this.logsLoading = false
    }
  }

  private handleLogsPageChange(page: number) {
    this.logsQuery.page = page
    this.fetchLogs()
  }

  private formatDate(date: string | undefined): string {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }
}
